import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Printer, Save } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { NeuPicker } from "../../components/neu/NeuPicker";
import { DateInput } from "../../components/neu/DateInput";
import { settlementCandidates, settlementPreview, createSettlement } from "../../api/settlements";
import { openSettlementReport } from "./openSettlementReport";
import { SettlementFields } from "./SettlementFields";
import { SettlementSummary } from "./SettlementSummary";
import { SettlementDismissed } from "./SettlementDismissed";
import { S } from "./settlementLabels";

const n = (v) => Number(v || 0);

// اختيار موظف منتهية خدمته (terminated) + تاريخ آخر يوم عمل، ثم عرض/حفظ/طباعة تسويته.
export const SettlementForm = ({ brand, onSaved }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const L = S[lang === "en" ? "en" : "ar"];
  const [people, setPeople] = useState([]);
  const [uid, setUid] = useState("");
  const [lwd, setLwd] = useState("");
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { settlementCandidates().then(setPeople).catch(() => setPeople([])); }, []);

  const pick = (id) => {
    setUid(id);
    const c = people.find((x) => x.id === id);
    setLwd(c?.termination_date || new Date().toISOString().slice(0, 10));
  };

  useEffect(() => {
    if (!uid || !lwd) { setP(null); return; }
    settlementPreview(uid, lwd).then(setP).catch(() => setP(null));
  }, [uid, lwd]);

  const net = p ? n(p.accrued_salary) + n(p.gratuity) - n(p.deductions) : 0;
  const printData = () => ({ ...p, reason: p.exit_type, last_working_day: lwd, net_amount: p.no_settlement ? 0 : net });

  const save = async () => {
    setBusy(true);
    try {
      const saved = await createSettlement({
        user_id: uid, last_working_day: lwd, reason: p.exit_type || "termination",
        accrued_salary: p.no_settlement ? 0 : n(p.accrued_salary),
        gratuity: p.no_settlement ? 0 : n(p.gratuity),
        deductions: p.no_settlement ? 0 : n(p.deductions),
      });
      toast.success(L.save);
      openSettlementReport(
        { ...saved, name_ar: p.name_ar, job_title: p.job_title, basic_salary: p.basic_salary, exit_type: p.exit_type },
        brand, lang);
      onSaved?.();
    } catch { toast.error(t("errors.generic")); } finally { setBusy(false); }
  };

  return (
    <NeuCard className="grid gap-4" data-testid="settlement-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <NeuPicker testId="settle-employee" value={uid} onChange={(e) => pick(e.target.value)}>
          <option value="">{L.pickEmployee}</option>
          {people.map((x) => (
            <option key={x.id} value={x.id} disabled={x.settled}>
              {x.name} — {L.exitTypes[x.exit_type] || x.exit_type || ""}{x.settled ? ` (${L.done})` : ""}
            </option>
          ))}
        </NeuPicker>
        <DateInput className="w-full" label={L.rows.last} testId="settle-lwd" value={lwd} onChange={setLwd} />
      </div>
      {p ? (
        <>
          <SettlementSummary p={p} reason={p.exit_type} lang={lang} />
          {p.no_settlement
            ? <SettlementDismissed lang={lang} />
            : <SettlementFields p={p} lang={lang} onChange={(k, v) => setP({ ...p, [k]: v })} />}
          <div className="flex flex-wrap gap-3">
            <NeuButton variant="primary" className="flex items-center gap-2" data-testid="settle-save"
                       disabled={busy} onClick={save}><Save className="h-4 w-4" />{L.save}</NeuButton>
            <NeuButton className="flex items-center gap-2" data-testid="settle-print"
                       onClick={() => openSettlementReport(printData(), brand, lang)}>
              <Printer className="h-4 w-4" />{L.print}</NeuButton>
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="settle-hint">{L.hint}</p>
      )}
    </NeuCard>
  );
};
